import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Boxes, MapPinCheck, ShoppingCart, Receipt } from "lucide-react";

const line = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.18, duration: 1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const chip = {
  hidden: { opacity: 0, y: 14 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: 1 + i * 0.09, duration: 0.5 } }),
};

const ticketRows = [
  ["Tapal Tea 190g", "480.00"],
  ["Milkpak 1L", "290.00"],
  ["Dawn Bread", "200.00"],
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yCard = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const ySide = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} data-testid="landing-hero" className="relative overflow-hidden">
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute -top-48 right-[-8%] h-[520px] w-[520px] rounded-full bg-amber-400/25 blur-[140px]"
      />
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute -left-40 top-1/3 h-[420px] w-[420px] rounded-full bg-sky-400/15 blur-[140px]"
      />
      <div className="dot-grid pointer-events-none absolute inset-0 opacity-40" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 pb-20 pt-32 sm:pt-36 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:pt-44">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500"
          >
            <span className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Smart-In Product Portfolio
          </motion.div>

          <h1
            data-testid="hero-tagline"
            className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl"
          >
            {["Software that runs", "your entire", "operation."].map((text, i) => (
              <span key={text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-[#F59E0B]" : ""}`}
                  variants={line}
                  custom={i}
                  initial="hidden"
                  animate="visible"
                >
                  {text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            From the warehouse floor to the checkout counter — Smart-In builds the systems that
            businesses run on every single day.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#products"
              data-testid="hero-products-cta"
              className="inline-flex items-center gap-2 rounded-md bg-[#0F172A] px-7 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#1e293b] hover:shadow-lg"
            >
              Explore Products
              <ArrowDown className="h-4 w-4" />
            </a>
            <a
              href="#contact"
              data-testid="hero-contact-cta"
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-[#0F172A] transition-all hover:-translate-y-0.5 hover:border-[#0F172A] hover:shadow-lg"
            >
              Talk to Us
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </motion.div>

          <div className="mt-12 flex flex-wrap gap-x-6 gap-y-3">
            {["Warehouse", "Workforce", "Retail", "Vision", "Finance"].map((label, i) => (
              <motion.span
                key={label}
                variants={chip}
                custom={i}
                initial="hidden"
                animate="visible"
                className="inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500"
              >
                <span className="h-1 w-1 rounded-full bg-[#F59E0B]" />
                {label}
              </motion.span>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
          <motion.div style={{ y: ySide }} className="order-2 flex flex-col gap-5 lg:order-1">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.2, duration: 0.7 }}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/90 px-4 py-3.5 shadow-xl backdrop-blur-md"
            >
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-emerald-50">
                <MapPinCheck className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-[#0F172A]">Attendance · Check-in verified</p>
                <p className="text-[11px] text-slate-500">Site B — 09:42</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.35, duration: 0.7 }}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/90 px-4 py-3.5 shadow-xl backdrop-blur-md"
            >
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-sky-50">
                <Boxes className="h-5 w-5 text-sky-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-[#0F172A]">WMS · Picking wave complete</p>
                <p className="text-[11px] text-slate-500">12 / 12 orders dispatched</p>
              </div>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: yCard }} className="order-1 flex-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: 2 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-3xl bg-[#0F172A] p-7 shadow-[0_30px_70px_-25px_rgba(15,23,42,0.5)]"
            >
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div className="flex items-center gap-3">
                  <div className="grid h-9 w-9 place-items-center rounded-lg bg-white/10">
                    <ShoppingCart className="h-5 w-5 text-[#F59E0B]" />
                  </div>
                  <div>
                    <p className="font-display text-sm font-bold text-white">Smart-In POS</p>
                    <p className="text-xs text-slate-400">Terminal 02 · Online</p>
                  </div>
                </div>
                <span className="live-dot h-2 w-2 rounded-full bg-emerald-400" />
              </div>

              <div className="inline-flex items-center gap-2 rounded-md bg-[#F59E0B]/15 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#F59E0B] mt-4">
                <Receipt className="h-3.5 w-3.5" />
                FEFO batch auto-picked
              </div>

              <div className="space-y-3 py-5">
                {ticketRows.map(([item, price], i) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.1 + i * 0.15, duration: 0.4 }}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-slate-300">{item}</span>
                    <span className="font-mono text-slate-100">{price}</span>
                  </motion.div>
                ))}
              </div>

              <div className="flex items-center justify-between border-t border-dashed border-white/15 pt-4">
                <span className="text-sm font-semibold text-slate-300">Total</span>
                <span className="font-display text-xl font-extrabold text-white">970.00</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
